/**
 * useDeviceTilt.js
 * ----------------
 * Reads the device's tilt (front-to-back and left-to-right) from
 * DeviceOrientationEvent's `beta` and `gamma` fields. Exposes them in
 * degrees, EMA-smoothed to hide sensor jitter, plus a boolean `level`
 * flag that turns true when the device is essentially flat.
 *
 * Values:
 *   • betaDeg   — front-to-back tilt (positive = top of phone raised)
 *   • gammaDeg  — left-to-right tilt (positive = right edge raised)
 *   • level     — |beta| ≤ LEVEL_DEG AND |gamma| ≤ LEVEL_DEG
 *   • ready     — we have received at least one sample
 *   • needPermission / requestPermission — iOS 13+ gate
 */
import { useEffect, useRef, useState } from 'react';

const LEVEL_DEG = 3;      // tolerance for "phone is flat"
const EMA_ALPHA = 0.25;   // low-pass smoothing (bigger = more responsive)

export const useDeviceTilt = () => {
  const [state, setState] = useState({
    betaDeg: 0,
    gammaDeg: 0,
    level: false,
    ready: false,
  });
  const [needPermission, setNeedPermission] = useState(false);

  const smoothed = useRef({ beta: 0, gamma: 0, ready: false });

  const attach = () => {
    const onOrientation = (e) => {
      const beta  = e.beta  ?? 0;   // front-to-back
      const gamma = e.gamma ?? 0;   // left-to-right
      if (!smoothed.current.ready) {
        smoothed.current.beta  = beta;
        smoothed.current.gamma = gamma;
        smoothed.current.ready = true;
      } else {
        smoothed.current.beta  = smoothed.current.beta  * (1 - EMA_ALPHA) + beta  * EMA_ALPHA;
        smoothed.current.gamma = smoothed.current.gamma * (1 - EMA_ALPHA) + gamma * EMA_ALPHA;
      }
      const b = smoothed.current.beta;
      const g = smoothed.current.gamma;
      setState({
        betaDeg: b,
        gammaDeg: g,
        level: Math.abs(b) <= LEVEL_DEG && Math.abs(g) <= LEVEL_DEG,
        ready: true,
      });
    };
    window.addEventListener('deviceorientation', onOrientation);
    return () => window.removeEventListener('deviceorientation', onOrientation);
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !('DeviceOrientationEvent' in window)) return undefined;
    // iOS 13+ requires an explicit permission gesture.
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      setNeedPermission(true);
      return undefined;
    }
    return attach();
  }, []);

  const requestPermission = async () => {
    try {
      const res = await DeviceOrientationEvent.requestPermission();
      if (res === 'granted') {
        setNeedPermission(false);
        attach();
      }
    } catch (_e) { /* user dismissed */ }
  };

  return { ...state, needPermission, requestPermission };
};
